import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { TextInput } from '../../../../components/workbook/forms';

interface Question {
  id: number;
  text: string;
  impact: string;
  dependency: string;
  effort: string;
  leverage: number | null;
  rank: number | null;
}

const initialQuestions: Question[] = [
  {
    id: 1,
    text: 'What evidence would show that a Pro tier solves a problem customers are willing to pay more to fix?',
    impact: '',
    dependency: '',
    effort: '',
    leverage: null,
    rank: null
  },
  {
    id: 2,
    text: 'Which customer segments are most likely to adopt a Pro tier?',
    impact: '',
    dependency: '',
    effort: '',
    leverage: null,
    rank: null
  },
  {
    id: 3,
    text: 'What is the smallest set of "Pro" features that would make the offer credible?',
    impact: '',
    dependency: '',
    effort: '',
    leverage: null,
    rank: null
  },
  {
    id: 4,
    text: 'What existing commitments would have to pause or be reduced for engineering to deliver Pro on time?',
    impact: '',
    dependency: '',
    effort: '',
    leverage: null,
    rank: null
  },
  {
    id: 5,
    text: 'How do customers interpret the word "Pro," and does it signal the value we intend?',
    impact: '',
    dependency: '',
    effort: '',
    leverage: null,
    rank: null
  }
];

export default function Worksheet4_1Page() {
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);

  const updateScore = (id: number, field: 'impact' | 'dependency' | 'effort', value: string) => {
    const updated = questions.map(q => {
      if (q.id === id) {
        const newQ = { ...q, [field]: value };
        const impact = parseInt(newQ.impact) || 0;
        const dependency = parseInt(newQ.dependency) || 0;
        const effort = parseInt(newQ.effort) || 1;
        
        if (impact > 0 && dependency > 0 && effort > 0) {
          newQ.leverage = Math.round((impact * dependency / effort) * 100) / 100;
        } else {
          newQ.leverage = null;
        }
        return newQ;
      }
      return q;
    });
    
    // Calculate ranks
    const withLeverage = updated.filter(q => q.leverage !== null);
    const sorted = [...withLeverage].sort((a, b) => (b.leverage || 0) - (a.leverage || 0));
    
    const ranked = updated.map(q => {
      if (q.leverage !== null) {
        const rankIndex = sorted.findIndex(s => s.id === q.id);
        return { ...q, rank: rankIndex + 1 };
      }
      return { ...q, rank: null };
    });
    
    setQuestions(ranked);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--mustard-dim)] border-b border-[var(--mustard)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link
            to="/elevation/workbook/oqf/module-4"
            className="text-[var(--mustard)] hover:text-[var(--mustard)] text-sm mb-2 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Module 4
          </Link>
          <h1 className="text-2xl font-bold text-[var(--text-pri)]">
            ✍️ Activity: Worksheet 4.1
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-[var(--surface)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-3">
            Instructions
          </h2>
          <p className="text-[var(--text-sec)]">
            Here are 5 OQF-ready questions from the "Pro Tier" scenario. As a team, score each one using the 1-5 scale and calculate its Leverage.
          </p>
          <p className="text-sm text-[var(--text-sec)] mt-2">
            <strong>Scoring:</strong> 1 = Low, 3 = Medium, 5 = High. For Effort, 1 = Low Effort (easy), 5 = High Effort (hard).
          </p>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--surface)] border-b-2 border-[var(--rule)]">
                <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)] w-1/3">Open Question</th>
                <th className="px-4 py-3 text-center font-semibold text-[var(--text-pri)]">Impact<br/>(1-5)</th>
                <th className="px-4 py-3 text-center font-semibold text-[var(--text-pri)]">Dependency<br/>(1-5)</th>
                <th className="px-4 py-3 text-center font-semibold text-[var(--text-pri)]">Effort-to-Learn<br/>(1-5)</th>
                <th className="px-4 py-3 text-center font-semibold text-[var(--text-pri)]">Leverage<br/>(I×D / E)</th>
                <th className="px-4 py-3 text-center font-semibold text-[var(--text-pri)]">Rank</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)]">
              {questions.map((q, index) => (
                <tr key={q.id} className={q.rank === 1 ? 'bg-[var(--green-dim)]' : ''}>
                  <td className="px-4 py-4 text-[var(--text-sec)]">
                    <div className="font-medium mb-1">{index + 1}. {q.id === 1 ? 'Value Signal:' : q.id === 2 ? 'Segment Fit:' : q.id === 3 ? 'Feature Focus:' : q.id === 4 ? 'Delivery Risk:' : 'Messaging:'}</div>
                    <div className="text-sm text-[var(--text-sec)]">{q.text}</div>
                  </td>
                  <td className="px-4 py-4">
                    <TextInput
                      type="number"
                      min="1"
                      max="5"
                      value={q.impact}
                      onChange={(e) => updateScore(q.id, 'impact', e.target.value)}
                      className="w-16 text-center"
                      placeholder="1-5"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <TextInput
                      type="number"
                      min="1"
                      max="5"
                      value={q.dependency}
                      onChange={(e) => updateScore(q.id, 'dependency', e.target.value)}
                      className="w-16 text-center"
                      placeholder="1-5"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <TextInput
                      type="number"
                      min="1"
                      max="5"
                      value={q.effort}
                      onChange={(e) => updateScore(q.id, 'effort', e.target.value)}
                      className="w-16 text-center"
                      placeholder="1-5"
                    />
                  </td>
                  <td className="px-4 py-4 text-center">
                    {q.leverage !== null ? (
                      <span className="font-bold text-lg text-[var(--mustard)]">
                        {q.leverage.toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-[var(--text-ter)]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {q.rank !== null ? (
                      <span className="font-bold text-lg text-[var(--text-pri)]">
                        #{q.rank}
                      </span>
                    ) : (
                      <span className="text-[var(--text-ter)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 mb-6">
          <h3 className="font-semibold text-[var(--text-sec)] mb-2">💡 Model Answer (for reference)</h3>
          <div className="text-sm text-[var(--text-sec)] space-y-1">
            <p><strong>#1 Delivery Risk:</strong> Impact: 5, Dependency: 5, Effort: 1 → Leverage: 25.0</p>
            <p><strong>#2 Value Signal:</strong> Impact: 5, Dependency: 5, Effort: 3 → Leverage: 8.33</p>
            <p><strong>#3 Feature Focus:</strong> Impact: 3, Dependency: 4, Effort: 2 → Leverage: 6.0</p>
            <p><strong>#4 Segment Fit:</strong> Impact: 3, Dependency: 3, Effort: 2 → Leverage: 4.5</p>
            <p><strong>#5 Messaging:</strong> Impact: 2, Dependency: 2, Effort: 2 → Leverage: 2.0</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <Link
            to="/elevation/workbook/oqf/module-4"
            className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <Link
            to="/elevation/workbook/oqf/module-4/worksheet-4-2"
            className="px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] inline-flex items-center gap-2"
          >
            Next: Worksheet 4.2
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






